import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, Sparkles, Zap, BarChart2, ArrowRight, Users } from 'lucide-react';
import PollVotingOptions from './PollVotingOptions';

/**
 * PollResultsView (V3.2 - Unified Grid, Expandable + Masked Mode)
 * 1. Winner Banner (always visible if totalVoters >= 5)
 * 2. Expandable unified options grid
 * 3. Insight Engine
 */
const PollResultsView = ({
    type,
    options,
    pollPercentages,
    leadingIndex,
    leadingLabel,
    leadingPercent,
    showAllResults,
    setShowAllResults,
    isMultiSelect,
    selection,
    postVoteInsight,
    onViewInsights,
    inModal,
    totalVoters = 0
}) => {
    const isMasked = totalVoters < 5;

    return (
        <div className="space-y-4 max-w-2xl mx-auto w-full">
            {isMasked ? (
                <div className="relative group overflow-hidden rounded-[20px] p-6 bg-gray-50 dark:bg-white/5 border border-dashed border-gray-200 dark:border-white/10 text-center space-y-4">
                    <div className="w-12 h-12 rounded-full bg-primary-600/10 flex items-center justify-center mx-auto animate-pulse">
                        <Users size={20} className="text-primary-600" />
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-sm font-black uppercase tracking-widest text-gray-900 dark:text-white">
                            Collecting Consensus
                        </h3>
                        <p className="text-2xs font-bold text-gray-500 uppercase tracking-[0.2em]">
                            Statistical power building...
                        </p>
                    </div>
                    <p className="text-[11px] font-medium text-gray-500 italic max-w-[80%] mx-auto">
                        "The VANE engine requires a foundational density of records to establish a secure cryptographic verdict."
                    </p>
                </div>
            ) : (
                <>

            {/* 1. COMPACT WINNER BANNER */}
            <div className="relative group overflow-hidden rounded-[20px] p-4 bg-gradient-to-br from-primary-600 via-[#9333EA] to-primary-700 shadow-lg border border-white/20">
                <div className="absolute inset-0 bg-white/5 opacity-30" />
                <div className="relative z-10 flex items-center justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-1.5 mb-1">
                            <Sparkles size={8} className="text-white fill-white animate-pulse" />
                            <p className="text-2xs font-black uppercase tracking-widest text-white/70">Consensus Verdict</p>
                        </div>
                        <h3 className="text-xl md:text-2xl font-black italic text-white leading-tight tracking-tighter">
                            <span className="text-white">{leadingPercent}%</span> <span className="text-white/60">favor</span> <span className="text-white uppercase">{leadingLabel}</span>
                        </h3>
                    </div>

                    <div className="flex items-center gap-3 pl-4 border-l border-white/10">
                        <div className="w-10 h-10 rounded-full border-2 border-white/10 flex items-center justify-center relative">
                            <svg className="absolute inset-0 w-full h-full -rotate-90">
                                <circle cx="20" cy="20" r="18" fill="transparent" stroke="currentColor" strokeWidth="2" className="text-white/10" />
                                <circle 
                                    cx="20" cy="20" r="18" fill="transparent" stroke="currentColor" strokeWidth="2" 
                                    strokeDasharray={113} strokeDashoffset={113 - (113 * (leadingPercent / 100))}
                                    className="text-white transition-all duration-1000 ease-out"
                                />
                            </svg>
                            <span className="text-[6px] font-black text-white text-center leading-[1]">CONFIDENCE</span>
                        </div>
                        <div className="hidden sm:block">
                            <p className="text-2xs font-bold text-white italic">{(leadingPercent > 60) ? 'Strong' : 'Emerging'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. DISCOVERY BRIDGE (Compact) */}
            <div className="space-y-1">
                <button
                    onClick={() => setShowAllResults(prev => !prev)}
                    className="w-full flex items-center justify-between px-4 py-2 rounded-xl bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-all group"
                >
                    <div className="flex items-center gap-2">
                        <BarChart2 size={12} className="text-primary-600" />
                        <span className="text-2xs font-black uppercase tracking-widest text-gray-500 group-hover:text-gray-900 dark:group-hover:text-white">
                            Full Data Breakdown
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-2xs font-bold text-gray-500">{options.length} Points</span>
                        {showAllResults ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                </button>

                <AnimatePresence>
                    {showAllResults && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden bg-gray-50/30 dark:bg-white/2 rounded-b-xl"
                        >
                            <div className="px-2 py-2">
                                <PollVotingOptions
                                    type={type} options={options} selection={selection}
                                    handleTileClick={() => { }} inModal={true}
                                    showResults={true} results={pollPercentages}
                                    leadingIndex={leadingIndex}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* 3. INSIGHT MINI-CARD */}
            <div className="p-4 bg-gray-50 dark:bg-black border border-gray-100 dark:border-white/10 rounded-[20px] relative overflow-hidden group">
                <div className="relative z-10 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 bg-amber-500/5 px-2 py-0.5 rounded-full border border-amber-500/10">
                            <Zap size={10} className="text-amber-500 fill-amber-500" />
                            <span className="text-2xs font-black uppercase tracking-widest text-amber-600">Pulse Insight</span>
                        </div>
                        <div className="flex items-center gap-1 opacity-40">
                            <div className="w-1 h-1 rounded-full bg-green-500" />
                            <span className="text-2xs font-black uppercase">Live</span>
                        </div>
                    </div>

                    <p className="font-black text-sm italic leading-tight text-gray-900 dark:text-white tracking-tight">
                        "{postVoteInsight}"
                    </p>

                    <button
                        onClick={onViewInsights}
                        className="w-full bg-primary-600/10 hover:bg-primary-600 text-primary-600 hover:text-white py-2 rounded-lg font-black text-2xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 group/btn"
                    >
                        <span>Full Insight Engine</span>
                        <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>
        </>
    )}
</div>
    );
};

export default PollResultsView;

